'use client';

/**
 * One-shot voice recording hook.
 *
 * Records a short voice message, sends it to the backend for
 * transcription via /api/transcribe, and calls sendText() with
 * the resulting transcript. Used for the microphone button in
 * text chat mode (NOT for live call streaming).
 */

import { useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getAssistantApiUrl } from '@/lib/assistant-config';

// ─── Recording container negotiation ────────────────────────────
//
// MediaRecorder's default container differs per engine: Chromium records
// webm/opus, but iOS WebKit (Safari AND Chrome-on-iOS) records AAC-in-MP4.
// The old code force-labeled every blob `audio/webm` / recording.webm, so
// iPhone uploads reached OpenAI as an MP4 wearing a .webm name → 400
// "Audio file might be corrupted or unsupported" (FB27 #5, verified in QA
// CloudWatch). Record with the first container the engine actually
// supports and name the upload by what was truly recorded — the backend
// forwards the original filename to OpenAI.

const RECORDING_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

const MIME_EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'mp4',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
};

function pickRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported)
    return undefined;
  return RECORDING_MIME_CANDIDATES.find((t) =>
    MediaRecorder.isTypeSupported(t)
  );
}

/** "audio/mp4;codecs=mp4a.40.2" → "recording.mp4" (base type, no params). */
function recordingFilename(mimeType: string): string {
  const base = mimeType.split(';')[0].trim().toLowerCase();
  return `recording.${MIME_EXTENSIONS[base] ?? 'webm'}`;
}

// ─── Hook ───────────────────────────────────────────────────────

interface UseVoiceRecordingOptions {
  /** Called with the transcribed text after recording stops */
  onTranscript: (text: string) => void;
}

export function useVoiceRecording({ onTranscript }: UseVoiceRecordingOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    if (isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const preferredMime = pickRecordingMimeType();
      const recorder = new MediaRecorder(
        stream,
        preferredMime ? { mimeType: preferredMime } : undefined
      );
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        setIsRecording(false);
        // recorder.mimeType is what the engine ACTUALLY produced (it may
        // add codec params, or differ when no candidate was supported).
        const actualMime = recorder.mimeType || preferredMime || 'audio/webm';
        const blob = new Blob(chunks, { type: actualMime });

        try {
          const formData = new FormData();
          formData.append('file', blob, recordingFilename(actualMime));

          const resp = await fetch(getAssistantApiUrl('/api/transcribe'), {
            method: 'POST',
            body: formData,
          });

          if (!resp.ok) {
            console.error('Transcription request failed', resp.statusText);
            toast.error("Couldn't transcribe your message. Please try again.");
          } else {
            const data = await resp.json();
            const transcript: string | undefined = data?.transcript;
            if (transcript && transcript.trim().length > 0) {
              onTranscript(transcript.trim());
            } else {
              // A successful-but-empty transcript otherwise looks like a
              // no-op — tell the user nothing was heard.
              toast.error("Didn't catch that. Please try recording again.");
            }
          }
        } catch (err) {
          console.error('Error transcribing audio:', err);
          toast.error("Couldn't transcribe your message. Please try again.");
        } finally {
          stream.getTracks().forEach((t) => t.stop());
          mediaRecorderRef.current = null;
          mediaStreamRef.current = null;
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      // Mic-button equivalent of the call-mode permission case: surface it
      // rather than leaving the button looking inert.
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        toast.error(
          'Sara needs microphone access. Enable it in your browser settings and try again.'
        );
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        toast.error('No microphone found. Connect a mic and try again.');
      } else {
        console.error('Error starting recording:', err);
        toast.error("Couldn't start recording. Please try again.");
      }
    }
  }, [isRecording, onTranscript]);

  const stopRecording = useCallback(() => {
    if (!isRecording || !mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
  }, [isRecording]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    toggleRecording,
    mediaRecorderRef,
    mediaStreamRef,
  };
}
